import { useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck, Pencil, Trash2, BellRing } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, Loading, StatusBadge, StatCard } from "@/components/common";
import { CertDialog } from "@/components/dialogs";
import { api, useFetch, errMsg, fmtDate, CERT_LABELS } from "@/lib/api";

export default function Compliance() {
  const { data, reload } = useFetch("/biz/compliance");
  const { data: props } = useFetch("/biz/properties");
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const remind = async () => {
    try {
      const r = await api.post("/biz/compliance/remind");
      if (r.data.status === "nothing_due") toast.success("Nothing is expiring — all good");
      else if (r.data.status === "sent") toast.success(`Reminder emailed (${r.data.count} item(s))`);
      else toast.warning("Email is not configured yet — ask your administrator to set up SMTP");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => {
    if (!window.confirm("Delete this certificate?")) return;
    try { await api.delete(`/biz/compliance/${c.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = (data || []).filter((c) => filter === "all" || c.status === filter);
  const count = (s) => (data || []).filter((c) => c.status === s).length;
  return (
    <div data-testid="compliance-page">
      <PageHeader eyebrow="Safety" title="Compliance" subtitle="Gas Safety, EICR, EPC and more. Expiring means within 60 days."
        actions={<>
          <Button variant="outline" onClick={remind} data-testid="compliance-remind-button"><BellRing className="w-4 h-4 mr-1" />Email me a reminder</Button>
          <Button onClick={() => setEdit({})} disabled={!props?.length} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-cert-button"><Plus className="w-4 h-4 mr-1" />Add certificate</Button>
        </>} />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Valid" value={count("valid")} tone="green" testid="cert-count-valid" />
        <StatCard label="Expiring soon" value={count("expiring")} tone="amber" testid="cert-count-expiring" />
        <StatCard label="Expired" value={count("expired")} tone="red" testid="cert-count-expired" />
      </div>
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>{["all", "expired", "expiring", "valid"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`cert-filter-${s}`}>{s}</TabsTrigger>)}</TabsList>
      </Tabs>
      {!data ? <Loading /> : rows.length === 0 ? <EmptyState icon={ShieldCheck} title="No certificates" text="Record certificates to get expiry alerts." /> : (
        <div className="panel p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Certificate</TableHead><TableHead>Property</TableHead><TableHead>Reference</TableHead><TableHead>Issued</TableHead><TableHead>Expires</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                  <TableCell className="font-semibold">{CERT_LABELS[c.cert_type]}</TableCell>
                  <TableCell className="text-sm">{c.property_name}</TableCell>
                  <TableCell className="text-sm font-mono">{c.reference}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(c.issue_date)}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(c.expiry_date)}</TableCell>
                  <TableCell><StatusBadge status={c.status} testid={`cert-status-${c.id}`} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => setEdit(c)} data-testid={`edit-cert-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(c)} data-testid={`delete-cert-${c.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <CertDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} properties={props} initial={edit?.id ? edit : null} onSaved={reload} />
    </div>
  );
}
